/**
 * DecisionFlow — Add-in SSO Handoff Handler
 * ui/src/auth/SsoHandoff.jsx
 * data-fit LLC · S432 · OI-ADDIN-SSO-HANDOFF
 *
 * Consumes a short-lived handoff token generated by the add-in and
 * establishes a web session for the already-authenticated user.
 *
 * Flow:
 *   1. User clicks "Open in DecisionFlow" in PersistentHeader (add-in)
 *   2. Add-in POSTs /api/v1/addin/auth/generate-sso-token → raw token
 *   3. Add-in opens {app_url}/sso?token={raw_token} in new browser tab
 *   4. This component extracts token from URL params
 *   5. POSTs /api/v1/addin/auth/consume-sso-token → server validates,
 *      deletes token (single-use), sets df_session httpOnly cookie
 *   6. GET /api/v1/auth/me → hydrates user profile from cookie
 *   7. loginWithSession(user) → sets AuthContext state
 *   8. Navigates to correct shell via SHELL_ROOT
 *
 * Token properties (enforced server-side):
 *   - 90-second TTL  — SSO_TOKEN_EXPIRED (401) if window missed
 *   - Single-use     — SSO_TOKEN_INVALID (401) if replayed
 *   - Kill-switch    — SSO_HANDOFF_DISABLED (503) if domain disabled
 *
 * Error states:
 *   - Missing token param     → /login with error message
 *   - SSO_TOKEN_EXPIRED       → /login with retry instruction
 *   - SSO_TOKEN_INVALID       → /login with retry instruction
 *   - SSO_HANDOFF_DISABLED    → /login with manual sign-in prompt
 *   - USER_ACCOUNT_INACTIVE   → /login with admin contact
 *   - Network / unexpected    → /login with generic error
 *
 * Route: /sso (public — no RouteGuard)
 * Registered in App.jsx alongside /login, /verify-email, /auth/callback.
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./AuthContext.jsx";
import { apiPost, apiMe } from "../api/client.js";

// ── Shell redirect map — mirrors SsoCallback.jsx ────────────────────────────
const SHELL_ROOT = {
  comp_admin:         "/admin/",
  finance:            "/finance/",
  manager:            "/manager/",
  rep:                "/rep/",
  tenant_super_admin: "/tenant-admin/",
  tool_admin:         "/tenant-admin/",
  platform_admin:     "/tenant-admin/",
};

// ── Error code → human message map ─────────────────────────────────────────
function messageFromCode(code) {
  switch (code) {
    case "SSO_TOKEN_EXPIRED":
      return "The sign-in link has expired (90-second window). Please click 'Open in DecisionFlow' again from the add-in.";
    case "SSO_TOKEN_INVALID":
      return "This sign-in link is invalid or has already been used. Please try again from the add-in.";
    case "SSO_HANDOFF_DISABLED":
      return "Single sign-on from the add-in is temporarily unavailable. Please sign in manually.";
    case "USER_ACCOUNT_INACTIVE":
      return "Your account is inactive. Contact your administrator.";
    case "USER_ACCOUNT_LOCKED":
      return "Your account is locked. Contact support@data-fit.com.";
    default:
      return "Sign-in failed. Please try again or sign in manually at this page.";
  }
}

export default function SsoHandoff() {
  const [searchParams]       = useSearchParams();
  const navigate             = useNavigate();
  const { loginWithSession } = useAuth();
  const [statusMessage, setStatusMessage] = useState("Completing sign-in…");

  useEffect(() => {
    let cancelled = false;

    async function handleHandoff() {
      const token = searchParams.get("token");

      if (!token) {
        navigate("/login", {
          replace: true,
          state: { ssoError: "Invalid sign-in link. Please try again from the add-in." },
        });
        return;
      }

      // Deduplication guard — token is single-use. Prevents double-submission
      // on React Strict Mode double-mount or duplicate navigation events.
      const dedupKey       = `df-sso-handoff-${token}`;
      const alreadySubmitted = sessionStorage.getItem(dedupKey) === "1";

      try {
        setStatusMessage("Verifying your session…");

        if (!alreadySubmitted) {
          sessionStorage.setItem(dedupKey, "1");
          await apiPost("/api/v1/addin/auth/consume-sso-token", { token });
        }

        if (cancelled) return;
        setStatusMessage("Loading your workspace…");

        // Hydrate user profile from the cookie set by consume-sso-token
        const meResponse = await apiMe();
        if (cancelled) return;

        const userProfile = meResponse?.user ?? meResponse;
        if (!userProfile?.user_type) {
          throw new Error("Invalid user profile returned from /auth/me");
        }

        // Set AuthContext state
        await loginWithSession(userProfile);
        if (cancelled) return;

        // Navigate to correct shell
        const shellRoot = SHELL_ROOT[userProfile.user_type] ?? "/login";
        navigate(shellRoot, { replace: true });

      } catch (e) {
        if (cancelled) return;
        console.error("SsoHandoff catch:", e);

        // Recovery: if consume-sso-token failed but a valid session cookie
        // already exists (e.g. dedup guard consumed it on parallel mount),
        // navigate directly to shell rather than showing an error.
        try {
          const meResponse = await apiMe();
          const userProfile = meResponse?.user ?? meResponse;
          if (userProfile?.user_type) {
            await loginWithSession(userProfile);
            const shellRoot = SHELL_ROOT[userProfile.user_type] ?? "/login";
            navigate(shellRoot, { replace: true });
            return;
          }
        } catch (_) {
          // /auth/me also failed — genuine error, fall through.
        }

        const errCode = e?.code || e?.error?.code || null;
        navigate("/login", {
          replace: true,
          state: { ssoError: messageFromCode(errCode) },
        });
      }
    }

    handleHandoff();
    return () => { cancelled = true; };
  }, []); // Run once on mount — token param is stable at handoff time.

  // ── Loading UI — matches SsoCallback.jsx chrome exactly ───────────────────
  return (
    <div style={{
      display:        "flex",
      alignItems:     "center",
      justifyContent: "center",
      height:         "100vh",
      flexDirection:  "column",
      gap:            "16px",
      fontFamily:     '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
      background:     "rgb(var(--color-surface, 255 255 255))",
      color:          "rgb(var(--color-text, 15 23 42))",
    }}>
      {/* Spinner */}
      <div style={{
        width:        "32px",
        height:       "32px",
        border:       "3px solid rgb(var(--color-border, 226 232 240))",
        borderTop:    "3px solid rgb(var(--color-brand-primary, 10 36 99))",
        borderRadius: "50%",
        animation:    "df-spin 0.8s linear infinite",
      }} />
      <style>{`
        @keyframes df-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Status message */}
      <div style={{
        fontSize:   "var(--font-size-sm, 0.875rem)",
        color:      "rgb(var(--color-text-secondary, 100 116 139))",
        fontWeight: 500,
      }}>
        {statusMessage}
      </div>

      {/* Product identity */}
      <div style={{
        position:      "absolute",
        bottom:        "24px",
        fontSize:      "var(--font-size-xs, 0.75rem)",
        color:         "rgb(var(--color-text-tertiary, 148 163 184))",
        letterSpacing: "0.01em",
      }}>
        data-fit LLC · DecisionFlow
      </div>
    </div>
  );
}
